/**
 * Simple seeded random number generator for deterministic test scenarios
 *
 * Uses Linear Congruential Generator (LCG) algorithm - not cryptographically
 * secure but perfectly suitable for game simulation and testing.
 *
 * Example usage:
 *   const rng = new SeededRandom(12345)
 *   rng.nextInt(1, 10)  // Always returns same value for same seed
 *   rng.nextItem(['a', 'b', 'c'])  // Deterministic selection
 */
export class SeededRandom {
  private seed: number

  /**
   * Create a new seeded RNG
   * @param seed - Integer seed value (same seed = same sequence)
   */
  constructor(seed: number) {
    this.seed = seed
  }

  /**
   * Returns a float between 0 (inclusive) and 1 (exclusive)
   */
  next(): number {
    // LCG parameters from Numerical Recipes (commonly used)
    this.seed = (this.seed * 9301 + 49297) % 233280
    return this.seed / 233280
  }

  /**
   * Returns a random integer between min and max (inclusive)
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min
  }

  /**
   * Returns a random element from an array
   */
  nextItem<T>(array: T[]): T {
    return array[this.nextInt(0, array.length - 1)]
  }

  /**
   * Returns true with given probability (0-1)
   */
  nextBool(probability: number = 0.5): boolean {
    return this.next() < probability
  }

  /**
   * Shuffle an array in place (Fisher-Yates, deterministic)
   */
  shuffle<T>(array: T[]): T[] {
    const result = [...array]
    for (let i = result.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i)
      ;[result[i], result[j]] = [result[j], result[i]]
    }
    return result
  }
}
